import { type FC, useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { ArrowCounterclockwise } from 'react-bootstrap-icons';
import { useNotifications } from '../../../modules/hooks';
import type { GuestThemeColors, GuestThemeRecord } from '../../../modules/interfaces';
import { DEFAULT_GUEST_THEME, FONT_OPTIONS } from '../../../modules/utils/guestTheme';
import { useGuestPortalThemes } from '../../Dashboards/GuestPortalThemes/useGuestPortalThemes';
import { ThemePreview } from './ThemePreview';
import './styles/styles.css';

export type GuestPortalThemeModalMode = 'create' | 'edit' | 'view';

interface GuestPortalThemeModalProps {
  show: boolean;
  mode: GuestPortalThemeModalMode;
  propertyId: string;
  data?: GuestThemeRecord;
  handleClose: () => void;
  onSaved?: () => void;
}

interface ColorField {
  key: keyof GuestThemeColors;
  label: string;
}

interface ColorGroup {
  title: string;
  fields: ColorField[];
}

const COLOR_GROUPS: ColorGroup[] = [
  { title: 'Brand', fields: [{ key: 'primary', label: 'Primary / accent' }] },
  {
    title: 'Backgrounds',
    fields: [
      { key: 'bodyBackground', label: 'Page background' },
      // Drives elevated surfaces: cards and the expanded accordion header.
      { key: 'containerBackground', label: 'Cards & accordion' },
      { key: 'modalBackground', label: 'Modal background' },
    ],
  },
  {
    title: 'Text',
    fields: [
      { key: 'primaryText', label: 'Primary text' },
      { key: 'secondaryText', label: 'Secondary text' },
    ],
  },
  {
    title: 'Inputs',
    fields: [
      { key: 'inputBackground', label: 'Input background' },
      { key: 'border', label: 'Borders' },
    ],
  },
  {
    title: 'Access code',
    fields: [
      { key: 'accessCodeBackground', label: 'Card background' },
      { key: 'accessCodeText', label: 'Card text' },
    ],
  },
  {
    title: 'Back button',
    fields: [
      { key: 'backButtonBackground', label: 'Button background' },
      { key: 'backButtonIcon', label: 'Arrow color' },
    ],
  },
  {
    title: 'Rating stars',
    fields: [
      { key: 'ratingStarActive', label: 'Filled' },
      { key: 'ratingStarInactive', label: 'Outline' },
    ],
  },
  {
    title: 'Signature box',
    fields: [
      { key: 'signatureBackground', label: 'Box background' },
      { key: 'signatureInk', label: 'Ink color' },
    ],
  },
  {
    title: 'Alerts',
    fields: [
      { key: 'error', label: 'Error' },
      { key: 'warning', label: 'Warning' },
    ],
  },
];

export const GuestPortalThemeModal: FC<GuestPortalThemeModalProps> = ({
  show,
  mode,
  propertyId,
  data,
  handleClose,
  onSaved,
}) => {
  const { setSimpleToasts } = useNotifications()!;
  const themeService = useGuestPortalThemes();
  const [name, setName] = useState('');
  const [colors, setColors] = useState<GuestThemeColors>(DEFAULT_GUEST_THEME.colors);
  const [fontFamily, setFontFamily] = useState(DEFAULT_GUEST_THEME.fontFamily);
  const [saving, setSaving] = useState(false);

  const readOnly = mode === 'view';

  useEffect(() => {
    if (!show) return;
    setName(data?.name ?? '');
    setColors(data?.theme.colors ?? DEFAULT_GUEST_THEME.colors);
    setFontFamily(data?.theme.fontFamily ?? DEFAULT_GUEST_THEME.fontFamily);
  }, [show, data]);

  const draftTheme = useMemo(() => ({ colors, fontFamily }), [colors, fontFamily]);

  const updateColor = (key: keyof GuestThemeColors, value: string) => {
    setColors((prev) => ({ ...prev, [key]: value }));
  };

  const resetToDefault = () => {
    setColors(DEFAULT_GUEST_THEME.colors);
    setFontFamily(DEFAULT_GUEST_THEME.fontFamily);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setSimpleToasts({
        type: 'danger',
        message: 'Please enter a theme name',
        show: true,
      });
      return;
    }
    setSaving(true);
    try {
      if (mode === 'edit' && data) {
        await themeService.update(data.id, { name: name.trim(), theme: draftTheme });
      } else {
        await themeService.create(propertyId, name.trim(), draftTheme);
      }
      setSimpleToasts({ type: 'success', message: 'Theme saved', show: true });
      onSaved?.();
      handleClose();
    } catch {
      setSimpleToasts({ type: 'danger', message: 'Failed to save theme', show: true });
    } finally {
      setSaving(false);
    }
  };

  const title =
    mode === 'create'
      ? 'New guest portal theme'
      : mode === 'edit'
        ? 'Edit guest portal theme'
        : 'View guest portal theme';

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="guest-theme-editor">
          <Col md={5} className="guest-theme-controls">
            <Form.Group className="mb-3">
              <Form.Label>Theme name</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Summer 2026"
                value={name}
                disabled={readOnly}
                onChange={(event) => setName(event.target.value)}
              />
            </Form.Group>

            {COLOR_GROUPS.map((group) => (
              <div key={group.title} className="guest-theme-group">
                <span className="guest-theme-group-title">{group.title}</span>
                {group.fields.map((field) => (
                  <Form.Group key={field.key} className="guest-theme-color-field">
                    <Form.Label>{field.label}</Form.Label>
                    <div className="guest-theme-color-inputs">
                      <Form.Control
                        type="color"
                        value={colors[field.key]}
                        disabled={readOnly}
                        onChange={(event) => updateColor(field.key, event.target.value)}
                      />
                      <Form.Control
                        type="text"
                        value={colors[field.key]}
                        disabled={readOnly}
                        onChange={(event) => updateColor(field.key, event.target.value)}
                      />
                    </div>
                  </Form.Group>
                ))}
              </div>
            ))}

            <div className="guest-theme-group">
              <span className="guest-theme-group-title">Typography</span>
              <Form.Group className="guest-theme-color-field">
                <Form.Label>Font family</Form.Label>
                <Form.Select
                  value={fontFamily}
                  disabled={readOnly}
                  onChange={(event) => setFontFamily(event.target.value)}
                >
                  {FONT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            {!readOnly && (
              <Button
                variant="link"
                className="guest-theme-reset px-0"
                onClick={resetToDefault}
              >
                <ArrowCounterclockwise className="me-1" />
                Reset to default
              </Button>
            )}
          </Col>

          <Col md={7} className="guest-theme-preview-col">
            <span className="guest-theme-preview-label">Live preview</span>
            <div className="guest-theme-preview-frame">
              <ThemePreview theme={draftTheme} />
            </div>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button className="cancel-button" onClick={handleClose}>
          {readOnly ? 'Close' : 'Cancel'}
        </Button>
        {!readOnly && (
          <Button className="save-button" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save theme'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
